export interface TypeTextArgs {
  selector?: string;
  text: string;
  withinSelector?: string;
  clearFirst?: boolean;
  timeout?: number;
}

export interface TypeTextResult {
  success: boolean;
  message: string;
}

/**
 * Type text into an input or textarea field.
 * This tool simulates typing character by character and triggers appropriate events.
 */
export async function typeText(args: TypeTextArgs): Promise<TypeTextResult> {
  const timeout = args.timeout || 5000;
  const startTime = Date.now();

  if (!args.selector) {
    throw new Error("selector is required for typeText");
  }

  // Get the container to search within
  const getContainer = (): HTMLElement | null => {
    if (args.withinSelector) {
      const container = document.querySelector(args.withinSelector);
      if (container && container instanceof HTMLElement) {
        return container;
      }
    }
    return document.body;
  };

  // Find the input/textarea element
  const findElement = (): HTMLInputElement | HTMLTextAreaElement | null => {
    const container = getContainer();
    if (!container) return null;

    const element = container.querySelector(args.selector!);
    if (element && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
      // Don't use type_text for datetime fields - they need direct value setting via fill_form
      if (
        element instanceof HTMLInputElement &&
        (element.type === "date" || element.type === "datetime-local" || element.type === "time")
      ) {
        throw new Error(
          `Cannot use type_text for ${element.type} fields. Use fill_form instead to set the value directly.`,
        );
      }
      return element;
    }

    return null;
  };

  // Wait for element to appear
  while (Date.now() - startTime < timeout) {
    const element = findElement();
    if (element) {
      // Clear if requested
      if (args.clearFirst) {
        element.value = "";
        element.dispatchEvent(new Event("input", { bubbles: true }));
      }

      // Focus the element
      element.focus();

      // Type text character by character
      for (let i = 0; i < args.text.length; i++) {
        const char = args.text[i];
        element.value += char;
        element.dispatchEvent(new KeyboardEvent("keydown", { key: char, bubbles: true }));
        element.dispatchEvent(new KeyboardEvent("keypress", { key: char, bubbles: true }));
        element.dispatchEvent(new Event("input", { bubbles: true }));
        element.dispatchEvent(new KeyboardEvent("keyup", { key: char, bubbles: true }));

        // Small delay between characters for more realistic typing
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      // Trigger final change event
      element.dispatchEvent(new Event("change", { bubbles: true }));
      element.blur();

      return {
        success: true,
        message: `Successfully typed text into element with selector "${args.selector}"`,
      };
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error(`Element not found after ${timeout}ms (looking for selector "${args.selector}")`);
}
